import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Download, Trophy, ChevronLeft, ListChecks, Mic, MessagesSquare, AlertTriangle, Linkedin, Eye } from "lucide-react";
import api from "../../api";
import { buildLinkedInAddToProfileUrl } from "../../utils/linkedin";

/** VI Sales Certification 2026 — full per-section report card.
 *  Shows MCQ correct answers, pitch transcript + rubric, roleplay transcript + anchors. */
/** Dark-themed observer panel — visible only when ?debug=observer is set. */
function ObserverPanel({ bundle, testid }) {
  if (!bundle) return null;
  return (
    <div className="mt-4 border-l-4 border-[#F59E0B] bg-[#1E293B] text-[#E2E8F0] p-4 font-mono text-[11px] space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Eye size={12} className="text-[#F59E0B]" />
        <span className="uppercase tracking-widest text-[#F59E0B] font-bold text-[10px]">Observer · Claude scoring internals</span>
      </div>
      <details open>
        <summary className="cursor-pointer text-[#94A3B8] uppercase tracking-widest text-[10px] font-bold">System prompt</summary>
        <pre className="mt-2 whitespace-pre-wrap text-[10px] leading-relaxed">{bundle.system_prompt || "—"}</pre>
      </details>
      <details>
        <summary className="cursor-pointer text-[#94A3B8] uppercase tracking-widest text-[10px] font-bold">User prompt (what Claude was scored against)</summary>
        <pre className="mt-2 whitespace-pre-wrap text-[10px] leading-relaxed">{bundle.user_prompt || "—"}</pre>
      </details>
      <details>
        <summary className="cursor-pointer text-[#94A3B8] uppercase tracking-widest text-[10px] font-bold">Raw response from Claude</summary>
        <pre className="mt-2 whitespace-pre-wrap text-[10px] leading-relaxed">{bundle.raw_response || "—"}</pre>
      </details>
    </div>
  );
}

export default function SalesCertReport() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isObserver = params.get("debug") === "observer";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const qs = isObserver ? "?debug=observer" : "";
    api.get(`/participant/salescert/report${qs}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || String(e)));
  }, [isObserver]);

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/participant/salescert/certificate.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "VI_SalesCert_2026_Certificate.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Certificate download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (err) return (
    <div className="max-w-2xl mx-auto p-8 fade-in" data-testid="salescert-report-error">
      <AlertTriangle className="text-[#EE2737] mb-3" size={28} />
      <p className="text-sm text-[#525252] mb-6">{err}</p>
      <button onClick={() => nav("/participant/salescert")} className="btn btn-secondary">Back</button>
    </div>
  );
  if (!data) return <div className="p-10 text-sm text-[#878787]">Loading report…</div>;

  // Feb 2026 — if L&D hasn't released results yet the API returns a minimal
  // stub. Render an embargo state instead of the score dashboard.
  if (data.released === false) return (
    <div className="max-w-2xl mx-auto p-8 space-y-6 fade-in" data-testid="salescert-report-embargo">
      <button
        onClick={() => nav("/participant/salescert")}
        className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1"
      >
        <ChevronLeft size={14} /> Back to certification
      </button>
      <div className="card p-8 border-l-4 border-[#5F004B]">
        <div className="overline mb-2 !text-[#5F004B]">RESULTS NOT YET RELEASED</div>
        <h1 className="font-heading font-black text-3xl tracking-tighter mb-3">Your report is being prepared</h1>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          {data.message}
        </p>
        <div className="mt-5 space-y-2" data-testid="salescert-report-recorded-sections">
          {(data.sections || []).map((s) => (
            <div key={s.key} className="flex items-center justify-between text-xs">
              <span className="text-[#525252]">{s.label}</span>
              <span className={`badge ${s.status === "recorded" ? "badge-success" : "badge-neutral"}`}>
                {s.status === "recorded" ? "Recorded" : "Not started"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const pct = Math.round((data.marks_awarded / (data.marks_max || 1)) * 100);

  return (
    <div className="max-w-4xl mx-auto p-2 space-y-8 fade-in" data-testid="salescert-report">
      <button
        onClick={() => nav("/participant/salescert")}
        className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1"
      >
        <ChevronLeft size={14} /> Back to certification
      </button>

      {isObserver && (
        <div className="card p-3 bg-[#1E293B] text-[#E2E8F0] border-l-4 border-[#F59E0B]" data-testid="observer-banner">
          <div className="flex items-center gap-2">
            <Eye size={14} className="text-[#F59E0B]" />
            <span className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Observer mode</span>
            <span className="text-xs text-[#94A3B8]">— Claude&apos;s raw scoring prompts + responses shown below each Claude-scored section.</span>
          </div>
        </div>
      )}

      {/* Hero score card */}
      <div className={`card p-8 ${data.certified ? "border-2 border-[#059669]" : ""}`} data-testid="salescert-report-hero">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2">VI SALES CERTIFICATION 2026 · YOUR REPORT</div>
            <h1 className="font-heading font-black text-4xl tracking-tighter">
              {data.marks_awarded} <span className="text-[#878787] text-2xl">/ {data.marks_max}</span>
            </h1>
            <div className="text-sm text-[#525252] mt-1">{pct}% aggregate · {data.completed_count} of {data.total_count} sections completed</div>
          </div>
          <div className="flex flex-col items-end gap-3">
            {data.certified ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-[#059669] text-white uppercase tracking-widest text-xs font-bold" data-testid="salescert-report-certified">
                <Trophy size={14} /> Certified
              </div>
            ) : data.completed_count === 3 ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-[#FEE2E2] text-[#991B1B] uppercase tracking-widest text-xs font-bold" data-testid="salescert-report-below">
                Not certified
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F5] text-[#525252] uppercase tracking-widest text-xs font-bold" data-testid="salescert-report-incomplete">
                Sections still open
              </div>
            )}
            {data.certified && (
              <>
                <button
                  onClick={downloadCertificate}
                  disabled={downloading}
                  className="btn btn-primary text-xs"
                  data-testid="salescert-report-download"
                >
                  <Download size={12} /> {downloading ? "Preparing…" : "Download certificate"}
                </button>
                <a
                  href={buildLinkedInAddToProfileUrl({
                    certId: `SC-${(data.user?.id || "").slice(0, 8).toUpperCase()}`,
                    verifyUrl: `${window.location.origin}/verify/SC-${(data.user?.id || "").slice(0, 8).toUpperCase()}`,
                    issuedAtIso: (data.roleplay?.submitted_at || data.sales_pitch?.submitted_at || data.mcq?.submitted_at),
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="btn text-xs !bg-[#0A66C2] !text-white !border-transparent hover:!opacity-90"
                  data-testid="salescert-report-linkedin"
                >
                  <Linkedin size={12} /> Add to LinkedIn
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Section 1 — MCQ */}
      {data.mcq && (
        <section className="card p-6" data-testid="salescert-report-mcq">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center"><ListChecks size={16} /></div>
            <div>
              <div className="overline">SECTION 1 · KNOWLEDGE MCQ</div>
              <div className="font-heading font-bold text-lg">
                {data.mcq.marks_awarded} / {data.mcq.marks_max}
                <span className="ml-3 text-xs font-normal text-[#525252]">{data.mcq.correct} of {data.mcq.total} correct</span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {data.mcq.items?.map((it, i) => {
              const optMap = Object.fromEntries((it.options || []).map((o) => [o.key, o.text || o.label]));
              return (
                <details key={it.id} className={`border ${it.is_correct ? "border-[#059669]/30" : "border-[#FCA5A5]"} p-3`}>
                  <summary className="cursor-pointer flex items-center gap-2 text-sm">
                    {it.is_correct
                      ? <CheckCircle2 size={14} className="text-[#059669]" />
                      : <XCircle size={14} className="text-[#EE2737]" />}
                    <span className="font-bold">Q{i + 1}</span>
                    <span className="text-[#525252] flex-1 truncate">{it.question}</span>
                  </summary>
                  <div className="mt-3 space-y-2 text-xs">
                    <div className="text-[#0A0A0A]">{it.question}</div>
                    <div className="pl-3 border-l-2 border-[#E5E7EB] space-y-1 mt-2">
                      <div><span className="font-bold text-[#525252]">Your answer:</span> <span className={it.is_correct ? "text-[#059669]" : "text-[#EE2737]"}><b>{it.chosen}.</b> {optMap[it.chosen] || <em className="text-[#878787]">Not answered</em>}</span></div>
                      {!it.is_correct && (
                        <div><span className="font-bold text-[#525252]">Correct:</span> <span className="text-[#059669]"><b>{it.correct_option}.</b> {optMap[it.correct_option]}</span></div>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#878787]">Competency · {(it.competency || "").replace(/_/g, " ")}</div>
                  </div>
                </details>
              );
            })}
          </div>
        </section>
      )}

      {/* Section 2 — Sales Pitch */}
      {data.sales_pitch && (
        <section className="card p-6" data-testid="salescert-report-pitch">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center"><Mic size={16} /></div>
            <div>
              <div className="overline">SECTION 2 · RECORDED SALES PITCH</div>
              <div className="font-heading font-bold text-lg">
                {data.sales_pitch.marks_awarded} / {data.sales_pitch.marks_max}
                <span className="ml-3 text-xs font-normal text-[#525252]">Rubric score {data.sales_pitch.score_1_to_5} / 5</span>
              </div>
            </div>
          </div>

          {/* Multi-attempt strip — shown when the candidate has more than one
              scored attempt so admins see both scores at a glance and can
              tell which one is the best-of-two. */}
          {Array.isArray(data.sales_pitch.attempts) && data.sales_pitch.attempts.length > 1 && (
            <div className="mb-4 grid grid-cols-2 gap-3" data-testid="salescert-pitch-attempts-strip">
              {data.sales_pitch.attempts.map((a) => (
                <div
                  key={a.attempt_no}
                  className={`border-l-4 p-3 ${a.is_best ? "border-[#059669] bg-[#ECFDF5]" : "border-[#E5E7EB] bg-[#F9FAFB]"}`}
                  data-testid={`salescert-pitch-attempt-${a.attempt_no}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#525252] font-bold flex items-center gap-2">
                    Attempt {a.attempt_no}
                    {a.is_best && <span className="text-[9px] bg-[#059669] text-white px-1.5 py-0.5 rounded">BEST — counts</span>}
                  </div>
                  <div className="mt-1 text-xl font-black text-[#0A0A0A]">
                    {a.marks_awarded} <span className="text-xs font-normal text-[#525252]">/ {a.marks_max}</span>
                  </div>
                  <div className="text-[10px] text-[#878787] mt-1">
                    Rubric {a.score_1_to_5}/5 · {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2">SCENARIO</div>
              <p className="text-xs text-[#525252] leading-relaxed">{data.sales_pitch.scenario_prompt}</p>
            </div>
            <div>
              <div className="overline mb-2">RUBRIC ANCHORS</div>
              <div className="text-xs text-[#525252] space-y-2">
                {data.sales_pitch.rubric?.target && (
                  <div><b className="text-[#059669]">Target (5):</b> {data.sales_pitch.rubric.target}</div>
                )}
                {data.sales_pitch.rubric?.proficient && (
                  <div><b className="text-[#B45309]">Proficient (3):</b> {data.sales_pitch.rubric.proficient}</div>
                )}
                {data.sales_pitch.rubric?.anti_pattern && (
                  <div><b className="text-[#EE2737]">Anti-pattern (1):</b> {data.sales_pitch.rubric.anti_pattern}</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
            <div className="overline mb-2">COACH FEEDBACK</div>
            <p className="text-sm text-[#0A0A0A] leading-relaxed">{data.sales_pitch.feedback}</p>
          </div>

          <details className="mt-4">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#525252] font-bold">Your transcript</summary>
            <p className="text-xs text-[#525252] mt-2 leading-relaxed whitespace-pre-wrap">{data.sales_pitch.transcript}</p>
          </details>

          {isObserver && <ObserverPanel bundle={data.sales_pitch._debug_observer} testid="observer-panel-pitch" />}
        </section>
      )}

      {/* Section 3 — Role Play */}
      {data.roleplay && (
        <section className="card p-6" data-testid="salescert-report-roleplay">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center"><MessagesSquare size={16} /></div>
            <div>
              <div className="overline">SECTION 3 · AI CUSTOMER ROLE PLAY</div>
              <div className="font-heading font-bold text-lg">
                {data.roleplay.marks_awarded} / {data.roleplay.marks_max}
                <span className="ml-3 text-xs font-normal text-[#525252]">Rubric score {data.roleplay.score_1_to_5} / 5</span>
              </div>
            </div>
          </div>

          {/* Multi-attempt strip — mirror of the pitch strip above. Renders
              when the candidate has more than one scored role-play session. */}
          {Array.isArray(data.roleplay.attempts) && data.roleplay.attempts.length > 1 && (
            <div className="mb-4 grid grid-cols-2 gap-3" data-testid="salescert-rp-attempts-strip">
              {data.roleplay.attempts.map((a) => (
                <div
                  key={a.attempt_no}
                  className={`border-l-4 p-3 ${a.is_best ? "border-[#059669] bg-[#ECFDF5]" : "border-[#E5E7EB] bg-[#F9FAFB]"}`}
                  data-testid={`salescert-rp-attempt-${a.attempt_no}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#525252] font-bold flex items-center gap-2">
                    Attempt {a.attempt_no}
                    {a.is_best && <span className="text-[9px] bg-[#059669] text-white px-1.5 py-0.5 rounded">BEST — counts</span>}
                  </div>
                  <div className="mt-1 text-xl font-black text-[#0A0A0A]">
                    {a.marks_awarded} <span className="text-xs font-normal text-[#525252]">/ {a.marks_max}</span>
                  </div>
                  <div className="text-[10px] text-[#878787] mt-1">
                    Rubric {a.score_1_to_5}/5 · {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2">SCENARIO</div>
              <p className="text-xs text-[#525252] leading-relaxed">{data.roleplay.scenario_prompt}</p>
              <div className="text-[10px] uppercase tracking-widest text-[#878787] mt-2">Customer vantage · {data.roleplay.role_vantage}</div>
            </div>
            <div>
              <div className="overline mb-2">SCORING ANCHORS</div>
              <div className="text-xs text-[#525252] space-y-2">
                {data.roleplay.target_behaviour && (
                  <div><b className="text-[#059669]">Target behaviour:</b> {data.roleplay.target_behaviour}</div>
                )}
                {data.roleplay.anti_pattern && (
                  <div><b className="text-[#EE2737]">Anti-pattern:</b> {data.roleplay.anti_pattern}</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
            <div className="overline mb-2">COACH FEEDBACK</div>
            <p className="text-sm text-[#0A0A0A] leading-relaxed">{data.roleplay.feedback}</p>
          </div>

          <details className="mt-4">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#525252] font-bold">Conversation transcript</summary>
            <p className="text-xs text-[#525252] mt-2 leading-relaxed whitespace-pre-wrap">{data.roleplay.transcript}</p>
          </details>

          {isObserver && <ObserverPanel bundle={data.roleplay._debug_observer} testid="observer-panel-roleplay" />}
        </section>
      )}
    </div>
  );
}
